import { useEffect, useRef, useState } from "react";
import React from "react";
import "./Footer.css";
import Logo from "../../../assets/logo.png";
import { Link } from "react-router-dom";
import { PartyPopper } from "lucide-react";
import { ENABLE_TRAINS } from "../../../config/features";

/*
 * Footer content is kept configuration-driven so verified
 * company information and links can be added later without
 * changing the footer layout.
 */

const footerLinks = {
  Travel: [
    { label: "Search Flights", path: "/flights" },
    { label: "Compare Flights", path: "/compare" },
    { label: "Search Hotels", path: "/hotels" },
    ...(ENABLE_TRAINS
  ? [{ label: "Search Trains", path: "/trains" }]
  : []),
    { label: "Search Buses", path: "/buses" },
  ],

  Company: [
    { label: "About Us", path: "/about" },
    { label: "Contact Us", path: "/contact" },
  ],

  Policies: [
    { label: "Privacy Policy", path: "/privacy" },
    { label: "Payment Security", path: "/payment-security" },
    { label: "Terms & Conditions", path: "/terms" },
    {
      label: "Cancellation & Refund Policy",
      path: "/cancellation-refund",
    },
    { label: "Disclaimer", path: "/disclaimer" },
  ],

  "Travel Terms": [
    { label: "Flight Booking Terms", path: "/flight-terms" },
    { label: "Bus Booking Terms", path: "/bus-terms" },
    { label: "Hotel Booking Terms", path: "/hotel-terms" },
    { label: "Holiday Package Terms", path: "/holiday-terms" },
    { label: "Visa Terms", path: "/visa-terms" },
  ],
};

/*
 * Social links are intentionally empty for now.
 * Add verified official URLs here later without changing
 * the footer component structure.
 */
const socialLinks = [];

/*
 * These values are intentionally based only on the
 * company-provided footer information.
 */
const companyInfo = {
  phone: "9355471999",
  email: "Holidays@makebeetrip.com",
  website: "https://makebeetrip.com/",
};

function useIntersection(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      obs.observe(ref.current);
    }

    return () => obs.disconnect();
  }, [threshold]);

  return [ref, visible];
}

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [footerRef, footerVisible] = useIntersection(0.05);
  const [planePos, setPlanePos] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setPlanePos((position) =>
        position >= 110 ? -10 : position + 0.3
      );
    }, 30);

    return () => clearInterval(interval);
  }, []);

  const handleSubscribe = (event) => {
    event.preventDefault();

    if (email.trim()) {
      setSubscribed(true);
      setEmail("");
    }
  };

  return (
    <footer
      className={`ft ${footerVisible ? "ft--visible" : ""}`}
      ref={footerRef}
    >
      {/* Animated sky strip */}
      <div className="ft__skystrip">
        <div className="ft__clouds">
          {[...Array(6)].map((_, index) => (
            <span
              key={index}
              className={`ft__cloud ft__cloud--${index + 1}`}
            />
          ))}
        </div>

        <svg
          className="ft__plane"
          style={{ left: `${planePos}%` }}
          viewBox="0 0 64 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M2 32L62 8L46 32L62 56L2 32Z"
            fill="#86d1b2"
            stroke="#82d592"
            strokeWidth="2"
          />
          <path
            d="M22 32L46 32"
            stroke="#fff"
            strokeWidth="1.5"
          />
          <path
            d="M30 22L46 32L30 42"
            fill="#82d592"
          />
        </svg>

        <svg
          className="ft__wave"
          viewBox="0 0 1440 60"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z"
            fill="#0b2e2e"
          />
        </svg>
      </div>

      {/* Main footer body */}
      <div className="ft__body">

        {/* Newsletter / Travel Updates */}
        <div
          className="ft__newsletter ft__anim ft__anim--up"
          style={{ "--delay": "0s" }}
        >
          <div className="ft__nl-inner">
            <div className="ft__nl-text">
              <span className="ft__nl-tag">
                Travel Updates
              </span>

              <h3 className="ft__nl-heading">
                Stay Connected With MakeBeeTrip
              </h3>

              <p className="ft__nl-sub">
                Stay informed about your travel services and important
                updates from MakeBeeTrip.
              </p>
            </div>

            <form
              className="ft__nl-form"
              onSubmit={handleSubscribe}
            >
              {subscribed ? (
                <div className="ft__nl-success">
                  <span>
                    <PartyPopper size={18} />
                  </span>
                  You're subscribed successfully.
                </div>
              ) : (
                <>
                  <input
                    className="ft__nl-input"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(event) =>
                      setEmail(event.target.value)
                    }
                    required
                    aria-label="Email address"
                  />

                  <button
                    className="ft__nl-btn"
                    type="submit"
                  >
                    Subscribe
                  </button>
                </>
              )}
            </form>
          </div>
        </div>

        {/* Brand + Links grid */}
        <div className="ft__grid">

          {/* Brand / Contact column */}
<div
  className="ft__brand ft__anim ft__anim--left"
  style={{ "--delay": "0.1s" }}
>
  <div className="ft__brand-top">
    <div className="ft__logo">
      <img
        src={Logo}
        alt="MakeBeeTrip Logo"
      />
    </div>

    <span className="ft__brand-pill">
      Your Journey, Our Commitment
    </span>
  </div>

  <p className="ft__brand-desc">
    MakeBeeTrip Private Limited provides travel booking
    and travel assistance services for flights, hotels,
    holiday packages, cab &amp; transfers, bus bookings
    and visa assistance.
  </p>

  <div className="ft__contact-card">
    <a href={`tel:${companyInfo.phone}`} className="ft__contact-item">
      <span className="ft__contact-icon">☎</span>
      <span>
        <small>Call Us</small>
        <strong>{companyInfo.phone}</strong>
      </span>
      <span className="ft__contact-arrow">→</span>
    </a>

    <a
      href={`mailto:${companyInfo.email}`}
      className="ft__contact-item"
    >
      <span className="ft__contact-icon">✉</span>
      <span>
        <small>Email Us</small>
        <strong>{companyInfo.email}</strong>
      </span>
      <span className="ft__contact-arrow">→</span>
    </a>

    <a
      href={companyInfo.website}
      target="_blank"
      rel="noopener noreferrer"
      className="ft__contact-item"
    >
      <span className="ft__contact-icon">↗</span>
      <span>
        <small>Website</small>
        <strong>makebeetrip.com</strong>
      </span>
      <span className="ft__contact-arrow">→</span>
    </a>
  </div>
</div>

          {/* Link columns */}
<div className="ft__link-area">
  <div className="ft__link-columns">
    {Object.entries(footerLinks).map(
      ([section, links], columnIndex) => (
        <div
          key={section}
          className="ft__col ft__anim ft__anim--up"
          style={{
            "--delay": `${0.15 + columnIndex * 0.07}s`,
          }}
        >
          <h4 className="ft__col-heading">
            {section}
          </h4>

          <ul className="ft__links">
            {links.map(({ label, path }) => (
              <li
                key={label}
                className="ft__link-item"
              >
                <Link
                  to={path}
                  className="ft__link"
                >
                  {label}
                  <span className="ft__link-arrow">
                    →
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )
    )}
  </div>

  <div className="ft__links-bottom">
  <div className="ft__links-bottom-main">
    

<div>
  <span className="ft__links-bottom-icon">✦</span>
  <strong>  Plan Your Journey</strong>
</div>

  </div>
<span>Explore our travel services and booking options.</span>

</div>

</div>
        </div>

        {/* Bottom bar */}
<div
  className="ft__bottom ft__anim ft__anim--up"
  style={{ "--delay": "0.65s" }}
>
  <p className="ft__copy">
    © 2026 MAKEBEETRIP PRIVATE LIMITED. All rights reserved.
  </p>

  <div className="ft__legal">
    <Link
      to="/privacy"
      className="ft__legal-link"
    >
      Privacy Policy
    </Link>

    <Link
      to="/terms"
      className="ft__legal-link"
    >
      Terms &amp; Conditions
    </Link>

    <a
  href="/sitemap.html"
  className="ft__legal-link"
>
  Sitemap
</a>
  </div>
</div>

      </div>
    </footer>
  );
}